import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { 
  FolderKanban, 
  Users, 
  FileText, 
  TrendingUp, 
  Clock,
  ArrowUpRight,
  Loader2,
  ListTodo,
  CheckCircle2,
  Calendar as CalendarIcon,
  AlertCircle,
  DollarSign,
  RefreshCw,
  Sparkles,
  ChevronRight,
  User,
  FolderOpen,
  Circle
} from 'lucide-react';
import axios from 'axios';
import { format } from 'date-fns';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, getAuthHeaders } = useAuth();
  const { isDark } = useTheme();
  const [stats, setStats] = useState(null);
  const [upcomingTasks, setUpcomingTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Force re-render on theme change or user change
  const [, forceUpdate] = useState(0);
  useEffect(() => {
    forceUpdate(prev => prev + 1);
  }, [isDark, user?.role]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
        // Admins/owners see ALL org tasks; other users see only their assigned tasks
        const tasksEndpoint = isAdmin
          ? `${API_URL}/tasks/upcoming?limit=20`
          : `${API_URL}/tasks/my-tasks?limit=10`;

        const [statsRes, tasksRes] = await Promise.all([
          axios.get(`${API_URL}/dashboard/stats`, getAuthHeaders()),
          axios.get(tasksEndpoint, getAuthHeaders()).catch(() => ({ data: [] }))
        ]);
        setStats(statsRes.data);
        setUpcomingTasks(tasksRes.data || []);
      } catch (error) {
        console.error('Failed to fetch dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    if (user) {
      fetchData();
    }
  }, [user, getAuthHeaders]);

  const handleToggleTask = async (task) => {
    const newStatus = task.status === 'completed' ? 'todo' : 'completed';
    try {
      await axios.put(`${API_URL}/tasks/${task.id}`, { status: newStatus }, getAuthHeaders());
      const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
      const tasksEndpoint = isAdmin
        ? `${API_URL}/tasks/upcoming?limit=20`
        : `${API_URL}/tasks/my-tasks?limit=10`;
      const tasksRes = await axios.get(tasksEndpoint, getAuthHeaders());
      setUpcomingTasks(tasksRes.data || []);
    } catch (error) {
      console.error('Failed to update task');
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent': return 'border-l-red-500';
      case 'high': return 'border-l-orange-500';
      case 'medium': return 'border-l-blue-500';
      default: return 'border-l-slate-300';
    }
  };

  if (loading || !user) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Loader2 className="w-10 h-10 animate-spin text-blue-500 mx-auto" />
          <p className="text-slate-500 mt-3 text-sm">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  const isTenantUser = user.role === 'admin' || user.role === 'team';
  const isClient = user.role === 'client' || user.role === 'client_team';
  const isClientTeam = user.role === 'client_team';  // More restricted than regular client
  
  // Client-specific stat cards - focused on their projects and invoices
  const clientStatCards = [
    {
      label: 'My Projects',
      value: stats?.total_projects || 0,
      icon: FolderKanban,
      gradient: 'from-blue-500 to-indigo-600',
      bgClass: 'stat-card-blue',
    },
    {
      label: 'Active Projects',
      value: stats?.active_projects || 0,
      icon: Clock,
      gradient: 'from-emerald-500 to-teal-600',
      bgClass: 'stat-card-emerald',
    },
    {
      label: 'Pending Tasks',
      value: upcomingTasks.filter(t => t.status !== 'completed').length,
      icon: ListTodo,
      gradient: 'from-amber-500 to-orange-600',
      bgClass: 'stat-card-amber',
    },
    // Only show invoice stats to regular clients, not client_team
    ...(isClientTeam ? [] : [{
      label: 'Outstanding Balance',
      value: `$${(stats?.pending_invoices || 0).toLocaleString()}`,
      icon: FileText,
      gradient: 'from-rose-500 to-pink-600',
      bgClass: 'stat-card-rose',
    }])
  ];
  
  const tenantStatCards = [
    {
      label: 'Total Balance Due',
      value: `$${(stats?.total_balance_due || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      subtitle: 'Unpaid invoices',
      icon: DollarSign,
      gradient: 'from-emerald-500 to-teal-600',
      bgClass: 'stat-card-emerald',
    },
    {
      label: 'Total Clients',
      value: stats?.total_clients || 0,
      icon: Users,
      gradient: 'from-purple-500 to-violet-600',
      bgClass: 'stat-card-purple',
    },
    {
      label: 'Total Projects',
      value: stats?.total_projects || 0,
      icon: FolderKanban,
      gradient: 'from-blue-500 to-indigo-600',
      bgClass: 'stat-card-blue',
    },
    {
      label: 'Yearly Recurring',
      value: `$${(stats?.yearly_recurring || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      subtitle: `${stats?.active_recurring_items || 0} active items`,
      icon: RefreshCw,
      gradient: 'from-amber-500 to-orange-600',
      bgClass: 'stat-card-amber',
    }
  ];

  const superAdminStatCards = [
    {
      label: 'Total Projects',
      value: stats?.total_projects || 0,
      icon: FolderKanban,
      gradient: 'from-blue-500 to-indigo-600',
      bgClass: 'stat-card-blue',
      change: '+12%'
    },
    {
      label: 'Active Projects',
      value: stats?.active_projects || 0,
      icon: Clock,
      gradient: 'from-emerald-500 to-teal-600',
      bgClass: 'stat-card-emerald',
      change: '+8%'
    },
    {
      label: 'Total Clients',
      value: stats?.total_clients || 0,
      icon: Users,
      gradient: 'from-purple-500 to-violet-600',
      bgClass: 'stat-card-purple',
      change: '+5%'
    },
    {
      label: 'Total Revenue',
      value: `$${(stats?.total_invoices || 0).toLocaleString()}`,
      icon: TrendingUp,
      gradient: 'from-amber-500 to-orange-600',
      bgClass: 'stat-card-amber',
      change: '+18%'
    }
  ];

  const statCards = isClient ? clientStatCards : (isTenantUser ? tenantStatCards : superAdminStatCards);

  // Get role-specific subtitle
  const getSubtitle = () => {
    if (isClient) return "Here's the latest on your projects.";
    if (isTenantUser) return "Here's what's happening with your business today.";
    return "Here's what's happening with your projects today.";
  };

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      {/* Header */}
      <div className="page-header animate-fade-in">
        <div className="flex items-center gap-3 mb-2">
          <h1 className="page-title">
            Welcome back, {user?.name?.split(' ')[0]}!
          </h1>
          <Sparkles className="w-6 h-6 text-amber-500" />
        </div>
        <p className="page-subtitle">
          {getSubtitle()}
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((stat, index) => (
          <div 
            key={stat.label} 
            className={`stat-card ${stat.bgClass} animate-fade-in`}
            style={{ 
              animationDelay: `${index * 0.1}s`,
              backgroundColor: isDark ? '#1e293b' : '#ffffff',
              borderColor: isDark ? '#334155' : '#e2e8f0'
            }}
            data-testid={`stat-${stat.label.toLowerCase().replace(/\s+/g, '-')}`}
          >
            <div className="flex items-start justify-between mb-4">
              <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${stat.gradient} flex items-center justify-center shadow-lg`}>
                <stat.icon size={22} className="text-white" />
              </div>
              {stat.change && (
                <span 
                  className="flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded-full"
                  style={{
                    backgroundColor: isDark ? 'rgba(16, 185, 129, 0.2)' : '#ecfdf5',
                    color: isDark ? '#34d399' : '#059669'
                  }}
                >
                  {stat.change}
                  <ArrowUpRight size={12} />
                </span>
              )}
            </div>
            <div>
              <p className="stat-value" style={{ color: isDark ? '#f1f5f9' : '#0f172a' }}>{stat.value}</p>
              <p className="stat-label" style={{ color: isDark ? '#94a3b8' : '#64748b' }}>{stat.label}</p>
              {stat.subtitle && (
                <p className="text-xs mt-1" style={{ color: isDark ? '#64748b' : '#94a3b8' }}>{stat.subtitle}</p>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Quick Stats Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Pending Invoices - Show for admin/super_admin, or "My Invoices" for clients */}
        <Card className="card-elevated overflow-hidden">
          <CardHeader className="border-b border-slate-100 pb-4 bg-gradient-to-r from-slate-50 to-white">
            <CardTitle className="text-base font-semibold text-slate-900 flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-rose-500 to-pink-600 flex items-center justify-center">
                <FileText size={16} className="text-white" />
              </div>
              {isClient ? 'My Invoices' : 'Pending Invoices'}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="text-center py-4">
              <p className="text-4xl font-bold gradient-text" style={{ fontFamily: 'Plus Jakarta Sans' }}>
                ${(stats?.pending_invoices || 0).toLocaleString()}
              </p>
              <p className="text-slate-500 mt-2 text-sm">{isClient ? 'Amount due' : 'Awaiting payment'}</p>
              <button 
                onClick={() => navigate('/invoices')}
                className="mt-4 text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1 mx-auto transition-colors"
              >
                View {isClient ? 'my invoices' : 'all invoices'}
                <ChevronRight size={16} />
              </button>
            </div>
          </CardContent>
        </Card>

        {/* Recent Activity - For admin/super_admin, or Project Updates for clients */}
        <Card className="card-elevated overflow-hidden">
          <CardHeader className="border-b border-slate-100 pb-4 bg-gradient-to-r from-slate-50 to-white">
            <CardTitle className="text-base font-semibold text-slate-900 flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
                <Clock size={16} className="text-white" />
              </div>
              {isClient ? 'Project Updates' : 'Recent Activity'}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {stats?.recent_activities?.length > 0 ? (
              <ul className="divide-y divide-slate-100">
                {stats.recent_activities.slice(0, 5).map((activity) => (
                  <li key={activity.id} className="px-6 py-3 hover:bg-slate-50/80 transition-colors">
                    <p className="text-sm text-slate-700">
                      <span className="font-semibold text-slate-900">{activity.user_name}</span>
                      {' '}{activity.action}{' '}
                      <span className="font-medium text-blue-600">{activity.entity_type}</span>
                      {' '}&ldquo;{activity.entity_name}&rdquo;
                    </p>
                    <p className="text-xs text-slate-400 mt-1">
                      {format(new Date(activity.created_at), 'MMM d, h:mm a')}
                    </p>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="px-6 py-10 text-center">
                <Clock size={32} className="mx-auto text-slate-200 mb-3" />
                <p className="text-sm text-slate-400">{isClient ? 'No project updates' : 'No recent activity'}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Upcoming Tasks */}
        <Card className="card-elevated overflow-hidden" data-testid="upcoming-tasks-card">
          <CardHeader className="border-b border-slate-100 pb-4 bg-gradient-to-r from-amber-50 to-orange-50">
            <CardTitle className="text-base font-semibold text-slate-900 flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
                <ListTodo size={16} className="text-white" />
              </div>
              {user?.role === 'admin' || user?.role === 'super_admin' ? 'All Team Tasks' : 'My Tasks'}
              {upcomingTasks.length > 0 && (
                <span className="ml-auto px-2.5 py-1 bg-amber-100 text-amber-700 text-xs font-semibold rounded-full">
                  {upcomingTasks.length}
                </span>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {upcomingTasks.length > 0 ? (
              <ul className="divide-y divide-slate-100">
                {upcomingTasks.slice(0, 10).map((task) => (
                  <li 
                    key={task.id} 
                    className={`px-4 py-3 hover:bg-slate-50 transition-colors border-l-4 ${getPriorityColor(task.priority)}`}
                    onClick={() => navigate(`/projects/${task.project_id}`)}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="flex items-start gap-3">
                      <button
                        onClick={(e) => { e.stopPropagation(); handleToggleTask(task); }}
                        className={`mt-0.5 flex-shrink-0 w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all ${
                          task.status === 'completed' 
                            ? 'bg-emerald-500 border-emerald-500 text-white' 
                            : 'border-slate-300 hover:border-emerald-500 hover:bg-emerald-50'
                        }`}
                      >
                        {task.status === 'completed' && <CheckCircle2 size={12} />}
                      </button>
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-medium truncate ${task.status === 'completed' ? 'text-slate-400 line-through' : 'text-slate-900'}`}>
                          {task.title}
                        </p>

                        {/* Assignees row */}
                        {task.assignees?.length > 0 && (
                          <div className="flex items-center gap-1 mt-1 flex-wrap">
                            <User size={11} className="text-slate-400 flex-shrink-0" />
                            {task.assignees.map((a, i) => (
                              <span key={a.id} className="text-xs text-blue-600 font-medium">
                                {a.name}{i < task.assignees.length - 1 ? ',' : ''}
                              </span>
                            ))}
                          </div>
                        )}
                        {task.assignees?.length === 0 && (
                          <div className="flex items-center gap-1 mt-1">
                            <User size={11} className="text-slate-300" />
                            <span className="text-xs text-slate-300">Unassigned</span>
                          </div>
                        )}

                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                          {/* Project name */}
                          {task.project_name && (
                            <span className="text-xs text-slate-400 flex items-center gap-1 truncate max-w-[120px]">
                              <FolderOpen size={10} />
                              {task.project_name}
                            </span>
                          )}
                          {/* Due date */}
                          {task.due_date && (
                            <span className={`text-xs flex items-center gap-1 ${
                              new Date(task.due_date) < new Date() && task.status !== 'completed'
                                ? 'text-red-500 font-semibold'
                                : 'text-slate-400'
                            }`}>
                              <CalendarIcon size={10} />
                              {format(new Date(task.due_date), 'MMM d')}
                            </span>
                          )}
                          {/* Status badge */}
                          {task.status === 'in_progress' && (
                            <span className="text-xs px-1.5 py-0.5 bg-blue-50 text-blue-600 rounded-full font-medium">
                              In Progress
                            </span>
                          )}
                          {task.status === 'completed' && (
                            <span className="text-xs px-1.5 py-0.5 bg-emerald-50 text-emerald-600 rounded-full font-medium">
                              Completed
                            </span>
                          )}
                          {/* Priority */}
                          {task.priority === 'urgent' && (
                            <span className="flex items-center gap-1 text-xs text-red-500 font-medium">
                              <AlertCircle size={10} />
                              Urgent
                            </span>
                          )}
                          {task.is_milestone && (
                            <span className="text-xs px-1.5 py-0.5 bg-purple-50 text-purple-600 rounded-full font-medium">
                              Milestone
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="px-6 py-10 text-center">
                <ListTodo size={32} className="mx-auto text-slate-200 mb-3" />
                <p className="text-sm text-slate-400">No upcoming tasks</p>
                <p className="text-xs text-slate-300 mt-1">You're all caught up!</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Super Admin Mode Banner */}
      {user?.role === 'super_admin' && (
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 p-6 animate-fade-in">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg width=%2260%22 height=%2260%22 viewBox=%220 0 60 60%22 xmlns=%22http://www.w3.org/2000/svg%22%3E%3Cg fill=%22none%22 fill-rule=%22evenodd%22%3E%3Cg fill=%22%23ffffff%22 fill-opacity=%220.03%22%3E%3Cpath d=%22M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z%22/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')]" />
          <div className="relative flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center shadow-lg shadow-blue-500/25">
              <Users size={26} className="text-white" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                Super Admin Mode
                <Sparkles className="w-4 h-4 text-amber-400" />
              </h3>
              <p className="text-slate-400 text-sm mt-0.5">
                Full access to all organizations, projects, and users across the platform.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
